import type {
  ArenaRecentFormLetter,
  ArenaStats,
  FavoritePickType,
} from '../types/arenaStats.types';

interface ProfileStatsResponse {
  totalChallenges?: number;
  wins?: number;
  losses?: number;
  ties?: number;
  winRate?: number;
  currentStreak?: number;
  totalOddsWon?: number;
  totalOddsLost?: number;
  netOdds?: number;
  recentForm?: unknown;
  favoritePickTypes?: unknown;
  openRecord?: { wins?: number; losses?: number; ties?: number };
}

function parseOpenRecord(raw: unknown): ArenaStats['openRecord'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as { wins?: number; losses?: number; ties?: number };
  return {
    wins: typeof r.wins === 'number' ? r.wins : 0,
    losses: typeof r.losses === 'number' ? r.losses : 0,
    ties: typeof r.ties === 'number' ? r.ties : 0,
  };
}

function parseFavoritePickTypes(raw: unknown): FavoritePickType[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is FavoritePickType =>
        item != null &&
        typeof item === 'object' &&
        typeof (item as FavoritePickType).marketId === 'number' &&
        typeof (item as FavoritePickType).marketName === 'string' &&
        typeof (item as FavoritePickType).count === 'number'
    )
    .slice(0, 3)
    .map((item) => ({
      marketId: item.marketId,
      marketName: item.marketName,
      count: item.count,
    }));
}

function parseRecentForm(raw: unknown): ArenaRecentFormLetter[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (letter): letter is ArenaRecentFormLetter =>
      letter === 'W' || letter === 'D' || letter === 'L'
  ).slice(-4);
}

export function parseArenaStatsFromProfileResponse(data: unknown): ArenaStats | null {
  const payload = (data as { success?: boolean; data?: { stats?: ProfileStatsResponse } })?.data;
  const stats = payload?.stats;
  if (!stats || typeof stats !== 'object') return null;

  const wins = typeof stats.wins === 'number' ? stats.wins : 0;
  const losses = typeof stats.losses === 'number' ? stats.losses : 0;
  const draws = typeof stats.ties === 'number' ? stats.ties : 0;

  return {
    wins,
    draws,
    losses,
    winRate: typeof stats.winRate === 'number' ? stats.winRate : 0,
    totalChallenges: typeof stats.totalChallenges === 'number' ? stats.totalChallenges : 0,
    netOdds: typeof stats.netOdds === 'number' ? stats.netOdds : 0,
    currentStreak: typeof stats.currentStreak === 'number' ? stats.currentStreak : undefined,
    totalOddsWon: typeof stats.totalOddsWon === 'number' ? stats.totalOddsWon : undefined,
    totalOddsLost: typeof stats.totalOddsLost === 'number' ? stats.totalOddsLost : undefined,
    recentForm: parseRecentForm(stats.recentForm),
    favoritePickTypes: parseFavoritePickTypes(stats.favoritePickTypes),
    openRecord: parseOpenRecord(stats.openRecord),
  };
}

